import { Accordion, useAccordionButton, Button, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { React, useState, useEffect, memo } from 'react';
import { ApiManager } from '../../ApiManager';
import { handleApiError } from '../../utils/apiErrorHandler';
import Chapters from './Chapters';
import ChapterModal from '../Modals/ChapterModal';
import SectionModal from '../Modals/SectionModal';
import ConfirmationModal from '../Modals/ConfirmationModal';
import PencilIcon from '../../Assets/Images/pencil-square.svg'
import TrashIcon from '../../Assets/Images/trash.svg'
import AddIcon from '../../Assets/Images/plus-circle.svg'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { applyReorder } from '../../utils/reorderUtils';

function SortableChapterWrapper({ chapter, isEditingSection, children }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: chapter.Id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'flex-start'
    };
    return (
        <div ref={setNodeRef} style={style}>
            {isEditingSection && (
                <button
                    ref={setActivatorNodeRef}
                    {...attributes}
                    {...listeners}
                    style={{ cursor: 'grab', border: 'none', background: 'none', padding: '8px 4px', color: '#666', fontSize: '16px', alignSelf: 'flex-start', marginTop: '2px' }}
                    title="Drag to reorder">
                    ⠿
                </button>
            )}
            {/* minWidth: 0 is required — a flex item defaults to min-width:auto, which refuses
                to shrink below its content's intrinsic width, so a long strategy row pushes the
                whole chapter card wider than the section it sits in. The two section-level
                wrappers (ProjectTabs, Admin) already carry this; the chapter one was missed. */}
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </div>
    );
}

const Sections = (props) => {
    const project = props.project;
    const [section, setSection] = useState(props.section);
    const canEditSections = props.canEditSections;
    const editingProject = props.editingProject;
    const setIsLoading = props.setIsLoading;
    const setIsLoadingProjectSections = props.setIsLoadingProjectSections;
    const [reloadChapter, setReloadChapter] = useState(false);
    const [reloadSection, setReloadSection] = useState(false);
    const [enableSectionHandler, setEnableSectionHandler] = useState(false);
    const [showSectionModal, setShowSectionModal] = useState(false);
    const [showChapterModal, setShowChapterModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isEditingSection, setIsEditingSection] = useState(false);
    const [chapters, setChapters] = useState([...(props.section.Chapters || [])].filter(c => c.Enabled).sort((a, b) => a.SortOrder - b.SortOrder));
    const sensors = useSensors(useSensor(PointerSensor));

    const sectionButtonText = isEditingSection ? "Stop Editing" : "Edit Section";

    useEffect(() => {
        setSection(props.section);
        setChapters(prev => prev.map(c => ({ ...c, SectionNumber: props.section.Number })));
    }, [props.section]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (enableSectionHandler) {
            const updatedSection = { ...section, Enabled: !section.Enabled };
            setIsLoadingProjectSections(true);
            if (!editingProject) {
                ApiManager.getInstance().deleteSection(updatedSection).then(() => {
                    setReloadSection(true);
                    setIsLoading(true);
                }).catch(handleApiError).finally(() => {
                    setEnableSectionHandler(false);
                    setIsLoadingProjectSections(false);
                });
            } else {
                ApiManager.getInstance().deleteProjectSection(updatedSection).then(() => {
                    setIsLoading(true);
                }).catch(handleApiError).finally(() => {
                    setEnableSectionHandler(false);
                    setIsLoadingProjectSections(false);
                });
            }
        }
    }, [enableSectionHandler]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (reloadSection) {
            setReloadSection(false);
            const apiCall = editingProject
                ? ApiManager.getInstance().getProjectSectionById(section)
                : ApiManager.getInstance().getSectionById(section);
            apiCall.then((result) => {
                setSection(result);
                setChapters([...(result.Chapters || [])].filter(c => c.Enabled).sort((a, b) => a.SortOrder - b.SortOrder));
                setReloadChapter(true);
            }).catch(handleApiError);
        }
    }, [reloadSection]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // Project-side chapter Numbers are frozen snapshots the server never reorders;
        // master-catalog Numbers move with position
        const result = applyReorder(chapters, active.id, over.id, { renumber: !editingProject });
        if (!result) return;

        const draggedChapter = chapters.find(c => c.Id === active.id);
        const previousChapters = chapters;
        setChapters(result.reordered);

        const updateCall = editingProject
            ? ApiManager.getInstance().updateProjectChapter({ ...draggedChapter, chapterId: draggedChapter.Id, SortOrder: result.targetSortOrder })
            : ApiManager.getInstance().updateChapter({ ...draggedChapter, chapterId: draggedChapter.Id, SortOrder: result.targetSortOrder, Number: result.targetNumber, SectionId: section.Id });
        updateCall.catch((error) => {
            setChapters(previousChapters);
            handleApiError(error);
        });
    };

    function CustomHeader({ children, eventKey }) {
        const customHeaderClicked = useAccordionButton(eventKey);
        return (
            <>
                <span style={{ cursor: "pointer", flex: 1, minWidth: 0 }} onClick={customHeaderClicked}>{children}</span>
                {
                    isEditingSection &&
                    <div className="ms-auto">
                        {!editingProject && <OverlayTrigger placement="top" overlay={<Tooltip id={"tooltip-sections-add"}>Add Chapter to Section</Tooltip>}>
                            <Button className="icon-btn" onClick={() => setShowChapterModal(true)}><img src={AddIcon} alt="Add Icon" /></Button>
                        </OverlayTrigger>}
                        <OverlayTrigger placement="top" overlay={<Tooltip id={"tooltip-sections-edit"}>Edit Section</Tooltip>}>
                            <Button className="icon-btn" onClick={() => setShowSectionModal(true)}><img src={PencilIcon} alt="Pencil Icon" /></Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id={"tooltip-sections-remove"}>Remove Section</Tooltip>}>
                            <Button className="icon-btn" onClick={() => setShowConfirmationModal(true)}><img src={TrashIcon} alt="Trash Icon" /></Button>
                        </OverlayTrigger>
                    </div>
                }
            </>
        );
    }

    return (
        <>
            <SectionModal
                editingProject={editingProject}
                section={section}
                setIsLoading={setIsLoading}
                showSectionModal={showSectionModal}
                setShowSectionModal={setShowSectionModal}
                createOrUpdate={2} />
            <ChapterModal
                editingProject={editingProject}
                section={section}
                showChapterModal={showChapterModal}
                setShowChapterModal={setShowChapterModal}
                setReloadSection={setReloadSection}
                createOrUpdate={1} />
            <ConfirmationModal
                type="section"
                setYesHandler={setEnableSectionHandler}
                showConfirmationModal={showConfirmationModal}
                setShowConfirmationModal={setShowConfirmationModal}
                Name={section.Name} />
            {section.Enabled &&
                <Card>
                    <Card.Header style={{ display: "flex" }}>
                        <CustomHeader section={section} eventKey={section.Id}>{section.Number} | {section.Name}</CustomHeader>
                    </Card.Header>
                    <Accordion.Collapse eventKey={section.Id}>
                        <Card.Body>
                            {chapters.filter(chapt => chapt.Enabled).length < 1 ? <p>No Chapters Assigned</p> :
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={chapters.map(c => c.Id)} strategy={verticalListSortingStrategy}>
                                        {chapters.filter(chapt => chapt.Enabled).map((chapter) =>
                                            <SortableChapterWrapper key={chapter.Id} chapter={chapter} isEditingSection={isEditingSection}>
                                                <Accordion>
                                                    <Chapters
                                                        chapter={chapter}
                                                        setReloadChapter={setReloadChapter}
                                                        setReloadSection={setReloadSection}
                                                        isEditingSection={isEditingSection}
                                                        reloadChapter={reloadChapter}
                                                        editingProject={editingProject}
                                                        section={section}
                                                        canEditSections={canEditSections}
                                                        project={project}
                                                    />
                                                </Accordion>
                                            </SortableChapterWrapper>
                                        )}
                                    </SortableContext>
                                </DndContext>
                            }
                            {canEditSections &&
                                <>
                                    <div className="d-flex mt-2">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => setIsEditingSection(!isEditingSection)}>{sectionButtonText}
                                        </Button>
                                    </div>
                                </>}
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>
            }
        </>

    );
}

export default memo(Sections);